import React, { useState, useEffect, useRef } from 'react';
import { getExperiment, getFeatureGate } from '../../../statsig/statsig-client';
import {
    STATSIG_EXPERIMENTS,
    STATSIG_EXPERIMENTS_DEFAULTS
} from '../../../statsig/experiments';
import * as ct from 'countries-and-timezones';
import {
    useAuth,
    AuthApiAvailableStates
} from '../../providers/auth-providers';
import analytics from '../../utils/analytics';
import CSMChatWidget from './csm-chat-widget';
import {
    fetchCohortAttributes,
    fetchCustomerOverview
} from './user-entitlement-service';
import { getLoginUrl } from '../../utils/get-auth-url';

const buildCleanContinueUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('distribute_session');
    return url.toString();
};

const hasDistributeSessionParam = () => {
    return new URLSearchParams(window.location.search).has(
        'distribute_session'
    );
};

const CSMChatContainer = () => {
    const [shouldLoadCsmAgent, setShouldLoadCsmAgent] = useState(false);
    const [isConfigAgentEnabled, setIsConfigAgentEnabled] = useState(false);
    const { aaId, email, icId = '', authApiTransitionState } = useAuth();
    const emailDomain = getEmailDomain(email);
    const aiAgentEvaluated = useRef(false);

    // Redirect unauthenticated users to login when distribute_session param is present
    useEffect(() => {
        if (
            authApiTransitionState !== AuthApiAvailableStates.FAILURE ||
            aaId ||
            !hasDistributeSessionParam()
        ) {
            return;
        }

        const handleSessionDistribution = async () => {
            const isEnabled = await getFeatureGate(
                'sac_distribute_session_redirect',
                false
            );

            if (isEnabled) {
                const cleanUrl = buildCleanContinueUrl();
                const loginUrl = getLoginUrl(cleanUrl);
                window.location.assign(loginUrl);
            }
        };

        handleSessionDistribution();
    }, [aaId, authApiTransitionState]);

    useEffect(() => {
        if (icId) {
            setShouldLoadCsmAgent(false);
            return;
        }

        if (aaId && !aiAgentEvaluated.current) {
            isCSMWidgetEnabled(aaId, emailDomain)
                .then((result) => {
                    setShouldLoadCsmAgent(!!result?.agentEnabled);
                    setIsConfigAgentEnabled(!!result?.configAgentEnabled);
                })
                .finally(() => {
                    aiAgentEvaluated.current = true;
                });
        }
    }, [aaId, emailDomain, icId]);

    return (
        shouldLoadCsmAgent && (
            <CSMChatWidget isConfigAgentEnabled={isConfigAgentEnabled} />
        )
    );
};

const isCSMWidgetEnabled = async (aaId, emailDomain) => {
    // Do not evaluate the experiment if the user is on the contact or requests page
    const windowPathname = window?.location?.pathname;
    const windowHostname = window?.location?.hostname;
    if (shouldExcludeByPathnameOrHostname(windowPathname, windowHostname)) {
        return { agentEnabled: false, configAgentEnabled: false };
    }

    const identifiers = {
        atlassianAccountId: aaId
    };

    const attributes = await createExperimentAttributes(aaId, emailDomain);

    const csmAgentExperiment = await getExperiment(
        STATSIG_EXPERIMENTS.CSM_AI_AGENT_ON_SAC,
        identifiers,
        attributes.experimentAttributes
    );
    sendExperimentEvent(
        csmAgentExperiment,
        aaId,
        attributes.experimentAttributes
    );
    const useCustomerOverviewContext = await getFeatureGate(
        'csm-ai-chat-context-update',
        false,
        identifiers,
        attributes.experimentAttributes
    );
    setCSMWidgetConfig(
        aaId,
        useCustomerOverviewContext
            ? attributes.customerOverview
            : attributes.experimentAttributes
    );

    const agentEnabled = csmAgentExperiment.getValue(
        'agent_enabled',
        STATSIG_EXPERIMENTS_DEFAULTS[STATSIG_EXPERIMENTS.CSM_AI_AGENT_ON_SAC][
            'agent_enabled'
        ]
    );

    const sacConfigCsmAgentEnabledExperiment = await getExperiment(
        STATSIG_EXPERIMENTS.ENABLE_SAC_CONFIG_CSM_AGENT_EXP,
        identifiers
    );
    const configAgentEnabled = sacConfigCsmAgentEnabledExperiment.getValue(
        'enabled',
        STATSIG_EXPERIMENTS_DEFAULTS.enable_sac_config_csm_agent_exp.enabled
    );

    return { agentEnabled, configAgentEnabled };
};

const shouldExcludeByPathnameOrHostname = (windowPathname, windowHostname) => {
    const isContactPathname =
        windowPathname.startsWith('/contact') ||
        windowPathname.startsWith('/hot-contact') ||
        windowPathname.startsWith('/partnercontact') ||
        windowPathname.startsWith('/requests') ||
        windowPathname.startsWith('/named-contact');

    const isPartnerHostname = windowHostname.startsWith(
        'atlassianpartners.atlassian.net'
    );

    return isContactPathname || isPartnerHostname;
};

const sendExperimentEvent = async (
    experimentResult,
    aaId,
    customAttributes
) => {
    const eventMetadata = {
        action: 'evaluated',
        actionSubject: 'CSMWidgetExperiment',
        actionSubjectId: 'CSMWidgetExperimentEvaluated'
    };
    const attributes = {
        ...experimentResult,
        aaId,
        isAdmin: !!customAttributes?.isAdminEntitlementPresent
    };
    analytics.sendGASV3OperationalEvent(eventMetadata, attributes);
};

const setCSMWidgetConfig = (atlassianAccountId, entitlements) => {
    window.atlassianWidgetConfig = {
        aaid: atlassianAccountId,
        config: {
            conversationContext: entitlements
        }
    };
};

const createExperimentAttributes = async (aaId, emailDomain) => {
    const isAtlassianAccount = emailDomain === 'atlassian.com';
    const hashedEmailDomain = await calculateHash(emailDomain);

    const [csmExperimentCohortAttributes, customerOverviewAttributes] =
        await Promise.all([fetchCohortAttributes(), fetchCustomerOverview()]);
    const experimentAttributes = {
        aaId,
        sacPath: window?.location?.pathname,
        hostname: window?.location?.hostname,
        isAtlassianAccount,
        hashedEmailDomain,
        countryCode: getLocationInfo().countryCode,
        ...csmExperimentCohortAttributes
    };
    const attributes = {
        experimentAttributes: experimentAttributes,
        customerOverview: customerOverviewAttributes
    };
    return attributes;
};

const getEmailDomain = (email) => {
    return email?.split('@')?.at(1) || '';
};

const calculateHash = async (message) => {
    const msgBuffer = new TextEncoder().encode(message);
    // Hash the message
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    // Convert ArrayBuffer to Array
    const hashArray = [...new Uint8Array(hashBuffer)];
    // Convert bytes to hex string
    const hashHex = hashArray
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    return hashHex;
};

const getLocationInfo = () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const countryCode = ct.getCountryForTimezone(timezone)?.id || '';

    return {
        countryCode: countryCode,
        timezone: timezone
    };
};

export default CSMChatContainer;
